/**
 * useZoneLayerRestore
 *
 * On floor/project change, fetches persisted layer-type-mappings and
 * restores any row whose `entity_data.properties.kind === 'zone'` as a
 * Polygon cell on the graph. Idempotent: skips entities already present
 * (matched via `data.entity_handle`).
 *
 * Separation of concerns: `useLayerMappings` handles CSV-derived layer
 * rows via `loadMappings`. This hook complements it by restoring Zone
 * polygons which are entity-level records with coordinates.
 */

import { useEffect } from 'react';
import { dia } from '@joint/core';

import { layerMappingApi } from '../../../map-editor-shared/api/services/layerMappingService';
import { createZoneCell } from './useZonePolygonDrawing';
import { computeLabelFontSize } from '../lib/parkingScaleMetrics';
import type {
  LayerMappingResponse,
  Point,
} from '../../../map-editor-shared/api/types/layerMapping';

interface UseZoneLayerRestoreArgs {
  graph: dia.Graph | null;
  projectId: number | null;
  floorId: string | null;
}

interface ZonePropertiesMaybe {
  kind?: string;
  closed?: boolean;
  name?: string;
  memo?: string;
  color?: string;
}

function extractZoneRecords(
  response: unknown
): LayerMappingResponse[] {
  const data = (response as { data?: unknown }).data;
  const arr =
    (data as { mappings?: LayerMappingResponse[] })?.mappings ??
    (Array.isArray(data) ? (data as LayerMappingResponse[]) : []);
  return arr.filter((m) => {
    const props = (m.entity_data as { properties?: ZonePropertiesMaybe } | undefined)
      ?.properties;
    return props?.kind === 'zone';
  });
}

function isAlreadyOnGraph(graph: dia.Graph, entityHandle: string): boolean {
  return graph.getCells().some((cell) => {
    const data = cell.get('data') as { entity_handle?: string } | undefined;
    return data?.entity_handle === entityHandle;
  });
}

function createZonePolygonCell(
  record: LayerMappingResponse,
  fontSize: number,
): dia.Element | null {
  const entityData = record.entity_data as
    | {
        points?: Point[];
        properties?: ZonePropertiesMaybe;
        layer?: string;
      }
    | undefined;
  const points = entityData?.points ?? [];
  const props = entityData?.properties ?? {};
  const name = props.name ?? 'Zone';

  // shared helper 사용 — drawing 경로와 동일 정책 (position+size, fill 없음, stroke만)
  // properties.color 가 저장되어 있으면 그걸로 stroke 표시 (사용자 지정 색상 복원)
  return createZoneCell({
    points,
    name,
    objectTypeId: record.object_type_id,
    entityHandle: record.entity_handle,
    layerName: entityData?.layer,
    memo: props.memo,
    strokeColor: props.color,
    fontSize,
  });
}

export function useZoneLayerRestore({
  graph,
  projectId,
  floorId,
}: UseZoneLayerRestoreArgs): void {
  useEffect(() => {
    if (!graph || projectId == null || !floorId) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await layerMappingApi.getList(projectId, floorId);
        if (cancelled) return;
        const records = extractZoneRecords(resp);
        // 구역 이름도 다른 라벨과 같은 배율로 — 맵 좌표계에서 고정 12px 는 안 보인다.
        const fontSize = computeLabelFontSize(graph);
        const cells: dia.Element[] = [];
        for (const rec of records) {
          if (!rec.entity_handle) continue;
          if (isAlreadyOnGraph(graph, rec.entity_handle)) continue;
          const cell = createZonePolygonCell(rec, fontSize);
          if (cell) cells.push(cell);
        }
        if (cells.length > 0) {
          graph.addCells(cells);
        }
      } catch {
        // Silently ignore on load — zones just won't appear. A future
        // toast layer can surface this without blocking the editor.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [graph, projectId, floorId]);
}
